import React, {
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { AppStore, userActions } from '@akademiasaas/shared';
import { useQuery } from '~/hooks/useQuery';
import { useAppDispatch } from '~/initializeStore';
import { deadlineGuardConfig } from '~/config/deadlineGuardConfig';
import { workspaceStore } from '~/config/workspaceStore';
import { bootstrapWorkspaces } from '~/api/workspaces';

interface OwnProps {}

type Props = PropsWithChildren<OwnProps>;

const AuthChecker: FunctionComponent<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { i18n } = useTranslation();
  const [isUserResolved, setIsUserResolved] = useState(false);
  const user = useSelector((store: AppStore) => store.user);
  const userUid = user.data?.uid;
  const query = useQuery();
  const syncedUsers = useRef<Set<string>>(new Set());
  const bootstrappedUsers = useRef<Set<string>>(new Set());

  const handleUserData = useCallback(
    async (newUser: firebase.User | null) => {
      setIsUserResolved(true);

      if (!newUser) {
        syncedUsers.current.clear();
        bootstrappedUsers.current.clear();
        workspaceStore.reset();

        return;
      }

      if (newUser.uid !== userUid) {
        dispatch(userActions.setLoggedUserData(newUser));
      }

      const runBootstrap = async () => {
        if (bootstrappedUsers.current.has(newUser.uid)) {
          return;
        }
        bootstrappedUsers.current.add(newUser.uid);
        try {
          await bootstrapWorkspaces();
          if (workspaceStore.getBootstrapError()) {
            bootstrappedUsers.current.delete(newUser.uid);
          }
        } catch {
          bootstrappedUsers.current.delete(newUser.uid);
          workspaceStore.setBootstrapReady(true);
        }
      };

      const runSync = async () => {
        if (syncedUsers.current.has(newUser.uid)) {
          return;
        }
        syncedUsers.current.add(newUser.uid);
        try {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const language = i18n.language || 'en';
          await deadlineGuardConfig.syncUser(timezone, language);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Failed to sync user with DeadlineGuard service:', error);
          syncedUsers.current.delete(newUser.uid);
        }
      };

      void Promise.all([runSync(), runBootstrap()]);

      if (history.location.pathname.startsWith('/auth')) {
        const continuePath = query.get('continue');
        history.push(continuePath || '/');
      }
    },
    [dispatch, history, i18n.language, query, userUid]
  );

  const debouncedCallback = useDebouncedCallback(handleUserData, 400);

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged(debouncedCallback);

    return () => unsubscribe();
  }, [debouncedCallback]);

  if (!isUserResolved) {
    return (
      <div
        style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthChecker;
